import { useEffect, useState } from 'react'
import type { LoginResponse } from '../api/auth'
import {
  CHANNEL_LABELS,
  leadSourcesApi,
  type LeadSourceDefinition,
  type LeadSourceDefinitionList,
} from '../api/leadSources'
import {
  Page,
  PageHeader,
  Button,
  IconButton,
  Callout,
  LoadingState,
  EmptyState,
  Card,
  DataTable,
  StatusPill,
  Dialog,
  TextField,
  Select,
  Checkbox,
  useConfirm,
  FormSection,
} from '../components/ui'

/**
 * R5-03 (E-04, REQ-062-002): l'elenco delle fonti dei lead, modificabile dall'Admin (nome, canale, attiva, ordine).
 * Le sei fonti storiche sono gia' presenti. Una fonte usata non si elimina: si disattiva (non e' piu' proposta per i
 * lead nuovi, ma i lead e i report esistenti continuano a leggerla). Dietro il flag `leads.fonti_modificabili`.
 */
interface FormState {
  id?: number
  code: string
  label: string
  channel: string
  sortOrder: string
  isActive: boolean
  uses: number
}

const EMPTY_FORM: FormState = { code: '', label: '', channel: 'altro', sortOrder: '', isActive: true, uses: 0 }

function isAdminRole(auth: LoginResponse): boolean {
  return auth.role === 'Administrator' || auth.role === 'EqualAdmin'
}

export function LeadSourcesPage({ auth }: { auth: LoginResponse }) {
  const askConfirm = useConfirm()
  const canWrite = isAdminRole(auth)
  const [data, setData] = useState<LeadSourceDefinitionList | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [modalError, setModalError] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      setData(await leadSourcesApi.list())
    } catch {
      setError("Non riesco a caricare l'elenco: la funzione potrebbe non essere attiva per te.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  function openCreate() {
    setForm(EMPTY_FORM)
    setModalError(null)
    setShowModal(true)
  }

  function openEdit(d: LeadSourceDefinition) {
    setForm({
      id: d.id, code: d.code, label: d.label, channel: d.channel,
      sortOrder: String(d.sortOrder), isActive: d.isActive, uses: d.uses,
    })
    setModalError(null)
    setShowModal(true)
  }

  function closeModal() {
    if (saving) return
    setShowModal(false)
    setModalError(null)
  }

  async function handleSave() {
    setSaving(true)
    setModalError(null)
    try {
      const order = form.sortOrder.trim() === '' ? undefined : parseInt(form.sortOrder, 10)
      const body = {
        label: form.label.trim(),
        channel: form.channel,
        sortOrder: Number.isNaN(order) ? undefined : order,
        isActive: form.isActive,
      }
      if (form.id) await leadSourcesApi.update(form.id, body)
      else await leadSourcesApi.create({ ...body, code: form.code.trim() || undefined })
      setShowModal(false)
      await load()
    } catch (e) {
      setModalError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(d: LeadSourceDefinition) {
    setError(null)
    try {
      await leadSourcesApi.update(d.id, { label: d.label, channel: d.channel, sortOrder: d.sortOrder, isActive: !d.isActive })
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    }
  }

  async function handleDelete(d: LeadSourceDefinition) {
    if (!(await askConfirm({ title: 'Elimina fonte', message: `Eliminare la fonte «${d.label}»?`, confirmLabel: 'Elimina', destructive: true }))) return
    setError(null)
    try {
      await leadSourcesApi.delete(d.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : "Errore nell'eliminazione")
    }
  }

  if (loading) {
    return (
      <Page>
        <LoadingState />
      </Page>
    )
  }

  const items = data?.items ?? []
  const channels = data?.channels ?? Object.keys(CHANNEL_LABELS)

  return (
    <Page>
      <PageHeader
        title="Fonti dei lead"
        icon="source_environment"
        back={{ to: '/settings', label: 'Impostazioni generali' }}
        actions={
          canWrite ? (
            <Button variant="filled" icon="add" onClick={openCreate}>
              Nuova fonte
            </Button>
          ) : undefined
        }
      />

      <Callout tone="info">
        Le fonti che compaiono nei moduli, nei filtri e nei report dei lead. Una fonte già usata non si elimina: si
        disattiva, e non viene più proposta per i lead nuovi (quelli esistenti restano com&apos;erano). Le modifiche
        arrivano a tutti gli utenti entro un minuto.
      </Callout>

      {error && <Callout tone="error">{error}</Callout>}

      <Card variant="outlined" padding="none">
        {items.length === 0 ? (
          <EmptyState icon="source_environment" title="Nessuna fonte" description="Aggiungi la prima fonte." />
        ) : (
          <DataTable variant="plain">
            <thead>
              <tr>
                <th>Ordine</th>
                <th>Fonte</th>
                <th>Canale</th>
                <th>Usi</th>
                <th>Stato</th>
                {canWrite && <th>Azioni</th>}
              </tr>
            </thead>
            <tbody>
              {items.map(d => (
                <tr key={d.id}>
                  <td className="md-num">{d.sortOrder}</td>
                  <td>
                    <span className="md-typescale-emphasized-body-medium">{d.label}</span>
                    <br />
                    <small className="text-muted">{d.code}{d.isStandard ? ' · storica' : ''}</small>
                  </td>
                  <td>{CHANNEL_LABELS[d.channel] ?? d.channel}</td>
                  <td className="md-num">{d.uses}</td>
                  <td>
                    <StatusPill tone={d.isActive ? 'success' : 'neutral'}>{d.isActive ? 'Attiva' : 'Disattivata'}</StatusPill>
                  </td>
                  {canWrite && (
                    <td>
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => openEdit(d)} />{' '}
                      <IconButton
                        icon={d.isActive ? 'visibility_off' : 'visibility'}
                        size="sm"
                        tooltip={d.isActive ? 'Disattiva' : 'Riattiva'}
                        aria-label={d.isActive ? 'Disattiva' : 'Riattiva'}
                        onClick={() => void toggleActive(d)}
                      />{' '}
                      <IconButton
                        icon="delete"
                        size="sm"
                        tooltip={d.isStandard || d.uses > 0 ? 'Fonte storica o in uso: disattivala invece di eliminarla' : 'Elimina'}
                        disabled={d.isStandard || d.uses > 0}
                        aria-label="Elimina"
                        onClick={() => void handleDelete(d)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </DataTable>
        )}
      </Card>

      <Dialog as="form"
        onSubmit={(e) => { e.preventDefault(); void handleSave() }}
        open={showModal}
        onClose={closeModal}
        dismissible={!saving}
        title={form.id ? 'Modifica fonte' : 'Nuova fonte'}
        actions={
          <>
            <Button variant="text" onClick={closeModal} disabled={saving}>
              Annulla
            </Button>
            <Button type="submit" variant="text" disabled={saving || !form.label.trim()}>
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
        <>
          {modalError && <Callout tone="error">{modalError}</Callout>}
          <FormSection title="Fonte">
            <TextField
              id="lsrc-label"
              label="Nome *"
              type="text"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
              disabled={saving}
            />
            <Select
              id="lsrc-channel"
              label="Canale"
              value={form.channel}
              onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}
              disabled={saving}
            >
              {channels.map(c => (
                <option key={c} value={c}>{CHANNEL_LABELS[c] ?? c}</option>
              ))}
            </Select>
            <TextField
              id="lsrc-order"
              label="Ordine"
              type="number"
              value={form.sortOrder}
              supportingText="Vuoto: in fondo all'elenco."
              onChange={e => setForm(f => ({ ...f, sortOrder: e.target.value }))}
              disabled={saving}
            />
            {!form.id && (
              <TextField
                id="lsrc-code"
                label="Codice"
                type="text"
                value={form.code}
                supportingText="Facoltativo: se vuoto nasce dal nome. Non si cambia più dopo la creazione."
                onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                disabled={saving}
              />
            )}
          </FormSection>
          {form.id && (
            <FormSection title="Opzioni" columns={1}>
              <Checkbox
                id="lsrc-active"
                label="Attiva (proposta per i lead nuovi)"
                checked={form.isActive}
                onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
                disabled={saving}
              />
            </FormSection>
          )}
        </>
      </Dialog>
    </Page>
  )
}
